import { useState, useCallback, useEffect } from "react";
import { FORM_FIELDS_PREFIX } from "utils/const";

/**
 * Extrai filename de um header Content-Disposition,
 * suportando tanto filename* (RFC5987) quanto filename simples.
 */
function extractFilename(contentDisposition) {
  // RFC5987: filename*=UTF-8''nome%20com%20espaços.pdf
  const rfc5987 = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(contentDisposition);
  if (rfc5987 && rfc5987[1]) {
    try {
      return decodeURIComponent(rfc5987[1].trim().replace(/^"|"$/g, ""));
    } catch {
      return rfc5987[1].trim().replace(/^"|"$/g, "");
    }
  }
  // fallback simples: filename="nome.pdf"
  const simple = /filename\s*=\s*"?([^";]+)"?/i.exec(contentDisposition);
  return simple ? simple[1] : null;
}

export default function useDownloadDocument(promise) {
  const [state, setState] = useState(new Map());
  const [error, setError] = useState(new Map());

  const updateState = useCallback((id, isLoading) => {
    setState((prev) => new Map(prev).set(id, isLoading));
  }, []);

  const updateError = useCallback((id, err) => {
    setError((prev) => new Map(prev).set(id, err));
  }, []);

  const download = useCallback(
    async (id, suggestedName) => {
      updateError(id, null);
      updateState(id, true);

      try {
        const result = await promise(id);

        let blob, headerValue;

        if (result && typeof result === "object" && "data" in result && "headers" in result) {
          blob = result.data;
          headerValue = result.headers["content-disposition"] || result.headers["Content-Disposition"] || null;
        } else {
          blob = result;
        }

        let filename = suggestedName || null;
        if (!filename && headerValue) {
          filename = extractFilename(headerValue);
        }
        if (!filename) {
          filename = `${FORM_FIELDS_PREFIX.DOCUMENT}${id}`;
        }

        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      } catch (err) {
        updateError(id, true);
      } finally {
        updateState(id, false);
      }
    },
    [promise, updateError, updateState]
  );

  useEffect(() => {
    return () => {
      setState(new Map());
      setError(new Map());
    };
  }, []);

  return [{ state, error }, download];
}
